import React, { useEffect } from 'react';
import { useImpressionRef } from '@noths/polaris-client-react-hooks';
import { getProductImageURL, isKeyboardTriggeredEvent } from '@noths/polaris-client-utils';
import { FavouriteButton } from '@noths/polaris-design-system-candidate-components';
import Image from 'next/image';

import { CarouselDots } from 'src/components/atoms/CarouselDots/CarouselDots';
import {
  NextButton,
  PreviousButton,
} from 'src/components/atoms/ImageNavigationButtons/ImageNavigationButtons';
import { ImageNumberLabel } from 'src/components/atoms/ImageNumberLabel/ImageNumberLabel';
import { MediaCountPill } from 'src/components/atoms/MediaCountPill/MediaCountPill';
import { PreventSwipeNavigation } from 'src/components/molecules/PreventSwipeNavigation/PreventSwipeNavigation';
import { ProductVideoSlide } from 'src/components/molecules/ProductVideoSlide/ProductVideoSlide';
import type {
  MediaItem,
  MediaLocation,
  NextOrPreviousPayload,
  OpenModalPayload,
  ProductMediaState,
  SetCurrentMediaPayload,
} from 'src/components/organisms/ProductMediaCarousel/types';
import {
  PRODUCT_MEDIA_TABPANEL_CLASS,
  PRODUCT_MEDIA_TABPANEL_CONTAINER_ID,
  PRODUCT_MEDIA_TABPANEL_ID_PREFIX,
} from './constants/elementIds';
import { mediaKey } from './media/buildProductMedia';
import { useIsGridLayout } from './useIsGridLayout';
import { useProductMediaCarousel } from './useProductMediaCarousel';
import * as styles from './ProductMediaCarousel.styles';

export interface ProductMediaCarouselProps {
  /**
   * Let the gallery lay itself out as a grid of tiles once there is a two-column page to sit
   * in (see `constants/layout.ts`). Consumers in a narrow surface of their own, such as the
   * product options drawer, leave this off and stay a carousel at every width.
   */
  allowGridLayout?: boolean;
  currentMedia: ProductMediaState['currentMedia'];
  favouriteProduct?: boolean;
  idPrefix?: string;
  location: MediaLocation;
  media: MediaItem[];
  mobileEdgeToEdge?: boolean;
  navigatedBy: ProductMediaState['navigatedBy'];
  onFavouriteClick?: (productCode: number) => void;
  onNextMedia: (payload: NextOrPreviousPayload) => void;
  onOpenModal?: (payload: OpenModalPayload) => void;
  onPrevMedia: (payload: NextOrPreviousPayload) => void;
  onVisible?: (payload: { totalMedia: number }) => void;
  productCode?: number;
  sectionLabel: string;
  setCurrentMedia: (payload: SetCurrentMediaPayload) => void;
  showArrows?: boolean;
  showDots?: boolean;
  showFavouriteButton?: boolean;
  showNumberOfSlides?: boolean;
  /** Pause the scroll/state sync while another surface (e.g. the full screen modal) owns navigation */
  suspendScrollSync?: boolean;
}

/**
 * The product media gallery: every image and video in one scroll-snap track.
 *
 * There is one DOM tree for both layouts. The track is a swipe carousel by default and the
 * stylesheet lays the same slides out as a grid of tiles once the page has two columns, for
 * consumers that pass `allowGridLayout`. Choosing between two trees in JavaScript, as this
 * component used to, means the server always renders the wrong one for somebody: every desktop
 * view painted the mobile carousel and then swapped, and the swap left the scroll listener
 * bound to a container that was no longer on the page, which froze the slide counter.
 *
 * Which affordances belong to which layout is therefore a question for the stylesheet, not for
 * this component: the counter and dots are hidden in the grid, the "View all photos" pill is
 * hidden in the carousel, and all of them are in the DOM either way.
 */
export const ProductMediaCarousel = ({
  allowGridLayout,
  currentMedia,
  favouriteProduct,
  idPrefix,
  location,
  media,
  mobileEdgeToEdge,
  navigatedBy,
  onFavouriteClick,
  onNextMedia,
  onOpenModal,
  onPrevMedia,
  onVisible,
  productCode,
  sectionLabel,
  setCurrentMedia,
  showArrows,
  showDots,
  showFavouriteButton,
  showNumberOfSlides,
  suspendScrollSync,
}: ProductMediaCarouselProps) => {
  const totalMedia = media.length;
  const impressionRef = useImpressionRef<HTMLElement>(() => {
    onVisible?.({ totalMedia });
  });
  const containerId = `${idPrefix ?? ''}${PRODUCT_MEDIA_TABPANEL_CONTAINER_ID}`;
  const slideId = (i: number) => `${idPrefix ?? ''}${PRODUCT_MEDIA_TABPANEL_ID_PREFIX}${i}`;
  const [mediaFocus, setMediaFocus] = React.useState(false);
  const [hasInteractedWithCarousel, setHasInteractedWithCarousel] = React.useState(false);
  // Undefined until measured on the client; both readers below treat that as "not the carousel".
  const isGridLayout = useIsGridLayout(allowGridLayout);
  const isCarousel = isGridLayout === false;

  useEffect(() => {
    if (currentMedia > 0) {
      // User has navigated past the first media item
      setHasInteractedWithCarousel(true);
    }
  }, [currentMedia]);

  const [setCarouselContainer, carouselItemRefs] = useProductMediaCarousel(
    (mediaIndex) => setCurrentMedia({ mediaIndex, navigationType: 'swipe', location }),
    currentMedia,
    navigatedBy,
    totalMedia,
    suspendScrollSync,
  );

  const openModalAt = (mediaIndex: number) => {
    setCurrentMedia({ mediaIndex, navigationType: 'click', location });
    onOpenModal?.({ location });
  };

  /*
    A tile is chosen by pointing at it, so the one you click becomes the current media. A
    carousel slide already is the current media, because the scroll position said so, and
    setting it again would report a second "Media click" for what is one expand interaction.
  */
  const openModalFromSlide = (mediaIndex: number) =>
    isGridLayout ? openModalAt(mediaIndex) : onOpenModal?.({ location });

  const favouriteButtonElement = showFavouriteButton &&
    onFavouriteClick &&
    productCode !== undefined && (
      <div css={styles.favouriteButtonContainer}>
        <FavouriteButton
          active={!!favouriteProduct}
          onClick={() => onFavouriteClick(productCode)}
          size="lg"
        />
      </div>
    );

  if (totalMedia === 0) {
    return null;
  }

  return (
    <PreventSwipeNavigation
      aria-live="polite"
      css={mobileEdgeToEdge && styles.mobileEdgeToEdge}
      data-allow-grid={Boolean(allowGridLayout)}
      data-media-count={totalMedia}
    >
      <section
        aria-label={sectionLabel}
        css={styles.gallery}
        ref={onVisible ? impressionRef : null}
      >
        <div
          css={styles.mediaTrack}
          data-media-focus={mediaFocus}
          id={containerId}
          ref={setCarouselContainer}
        >
          {media.map((item, i) => {
            const isVideo = item.type === 'video';
            /*
              A slide is a control when clicking it opens the modal: any image, and a video
              tile in the grid. A video slide in the carousel stays a plain container, because
              there ProductVideoSlide owns play/pause and the expand control, and a button
              inside a button is not valid markup.
            */
            const opensModalOnClick = Boolean(onOpenModal) && (!isVideo || isGridLayout === true);
            const label = isVideo ? item.title : `${item.title}, ${i + 1} of ${totalMedia}`;
            const setSlideRef = (node: HTMLElement | null) => {
              carouselItemRefs.current[i] = node as HTMLElement;
            };

            const slideContent = isVideo ? (
              <ProductVideoSlide
                contentPosition={i + 1}
                isActive={isCarousel && currentMedia === i}
                location={location}
                onExpand={isCarousel && onOpenModal ? () => onOpenModal({ location }) : undefined}
                playbackId={item.playbackId}
                posterUrl={item.posterUrl}
                title={item.title}
                totalOptions={totalMedia}
              />
            ) : (
              <Image
                alt={label}
                css={styles.mainMedia}
                fill={true}
                priority={i === 0}
                src={getProductImageURL(
                  item.href,
                  // The drawer has no modal to open and is a small surface, so it never
                  // upgrades. The hero loads its first slide full size and upgrades the rest
                  // once the customer starts moving through them.
                  onOpenModal && (i === 0 || hasInteractedWithCarousel) ? 'original' : 'normal',
                )}
                unoptimized
              />
            );

            return opensModalOnClick ? (
              <button
                aria-label={label}
                className={PRODUCT_MEDIA_TABPANEL_CLASS}
                css={styles.carouselSlide}
                data-item-index={i}
                data-media-focus={mediaFocus}
                id={slideId(i)}
                key={mediaKey(item)}
                onBlur={() => setMediaFocus(false)}
                onClick={() => openModalFromSlide(i)}
                onFocus={(e) => setMediaFocus(isKeyboardTriggeredEvent(e))}
                ref={setSlideRef}
                tabIndex={isCarousel && currentMedia !== i ? -1 : undefined}
                type="button"
              >
                {slideContent}
              </button>
            ) : (
              <div
                className={PRODUCT_MEDIA_TABPANEL_CLASS}
                css={styles.carouselSlide}
                data-item-index={i}
                data-media-focus={mediaFocus}
                id={slideId(i)}
                key={mediaKey(item)}
                ref={setSlideRef}
              >
                {slideContent}
              </div>
            );
          })}
        </div>
        {showArrows && totalMedia > 1 && (
          <>
            <PreviousButton
              controls={containerId}
              onClick={() => onPrevMedia({ location, totalMedia })}
            />
            <NextButton
              controls={containerId}
              onClick={() => onNextMedia({ location, totalMedia })}
            />
          </>
        )}
        {showNumberOfSlides && totalMedia > 1 && (
          <div css={styles.imageNumberLabelContainer}>
            <ImageNumberLabel currentImage={currentMedia + 1} totalImages={totalMedia} />
          </div>
        )}
        {allowGridLayout && onOpenModal && (
          <div css={styles.mediaCountPillContainer}>
            {/* Always opens on the first item, whichever tile it happens to sit over. */}
            <MediaCountPill onClick={() => openModalAt(0)} totalMedia={totalMedia} />
          </div>
        )}
        {favouriteButtonElement}
      </section>
      {showDots && totalMedia > 1 && (
        <div css={styles.carouselDotsContainer}>
          <CarouselDots currentMedia={currentMedia} totalMedia={totalMedia} />
        </div>
      )}
    </PreventSwipeNavigation>
  );
};
